import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Lightbulb, HelpCircle, CheckSquare, AlertTriangle, RefreshCw, Copy, Check } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function NextSessionPlan({ clientId, clientName }) {
  const [loading, setLoading] = useState(false);
  const [sessionPlan, setSessionPlan] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (clientId) {
      fetchSessionPlan();
    }
  }, [clientId]);

  const fetchSessionPlan = async () => {
    setLoading(true);
    try {
      console.log('Fetching session plan for client:', clientId);
      const response = await axios.get(`${API_URL}/clients/${clientId}/session-plan`);
      console.log('Session plan response:', response.data);
      setSessionPlan(response.data);
    } catch (error) {
      console.error('Error fetching session plan:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Failed to load session plan');
    } finally {
      setLoading(false);
    }
  };

  const generateSessionPlan = async () => {
    setLoading(true);
    try {
      console.log('Generating session plan for client:', clientId);
      const response = await axios.post(`${API_URL}/clients/${clientId}/generate-session-plan`);
      console.log('Generated session plan:', response.data);
      setSessionPlan(response.data);
      toast.success('Session plan generated!');
    } catch (error) {
      console.error('Error generating session plan:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.error || 'Failed to generate session plan');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (!sessionPlan) return;

    const text = `
# Next Session Plan for ${clientName}

## Recommended Focus
${sessionPlan.recommended_focus || 'N/A'}

## Powerful Questions
${sessionPlan.powerful_questions || 'N/A'}

## Suggested Homework
${sessionPlan.suggested_homework || 'N/A'}

## Predicted Blind Spots
${sessionPlan.blind_spots || 'N/A'}
    `.trim();

    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading && !sessionPlan) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[var(--border-color)] border-t-[var(--accent)]"></div>
          <p className="text-sm font-medium text-[var(--text-tertiary)]">Generating session plan...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[var(--text-primary)] mb-2">
            Next Session Plan
          </h1>
          <p className="text-[var(--text-secondary)]">
            AI-generated coaching plan for {clientName}
          </p>
        </div>
        <div className="flex gap-3">
          {sessionPlan && (
            <button
              onClick={copyToClipboard}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--bg-secondary)] text-[var(--text-primary)] border border-[var(--border-color)] rounded-lg font-medium hover:bg-[var(--bg-tertiary)] transition-colors"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4" />
                  <span>Copied!</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  <span>Copy Plan</span>
                </>
              )}
            </button>
          )}
          <button
            onClick={generateSessionPlan}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--accent)] text-white rounded-lg font-medium hover:bg-[var(--accent-hover)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>{sessionPlan ? 'Regenerate' : 'Generate'} Plan</span>
          </button>
        </div>
      </div>

      {!sessionPlan ? (
        <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-12 text-center">
          <Lightbulb className="w-16 h-16 mx-auto mb-4 text-[var(--accent)] opacity-50" />
          <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-2">
            Ready to plan your next session?
          </h2>
          <p className="text-[var(--text-secondary)] mb-6 max-w-md mx-auto">
            Generate an AI-powered session plan with recommended focus areas, powerful questions, and homework suggestions.
          </p>
          <button
            onClick={generateSessionPlan}
            className="px-6 py-3 bg-[var(--accent)] text-white rounded-lg font-semibold hover:bg-[var(--accent-hover)] transition-colors"
          >
            Generate Session Plan
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Recommended Focus */}
          <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-[var(--accent)]/10 rounded-lg">
                <Lightbulb className="w-5 h-5 text-[var(--accent)]" />
              </div>
              <h2 className="text-xl font-semibold text-[var(--text-primary)]">Recommended Focus</h2>
            </div>
            <div className="prose prose-invert max-w-none">
              <ReactMarkdown
                components={{
                  p: ({node, ...props}) => <p className="text-[var(--text-primary)] leading-relaxed mb-3" {...props} />,
                  strong: ({node, ...props}) => <strong className="font-semibold text-[var(--text-primary)]" {...props} />,
                  ul: ({node, ...props}) => <ul className="list-disc ml-5 space-y-2 text-[var(--text-primary)]" {...props} />,
                  li: ({node, ...props}) => <li className="text-[var(--text-primary)]" {...props} />,
                }}
              >
                {sessionPlan.recommended_focus || 'No focus areas available'}
              </ReactMarkdown>
            </div>
          </div>

          {/* Powerful Questions */}
          <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <HelpCircle className="w-5 h-5 text-blue-400" />
              </div>
              <h2 className="text-xl font-semibold text-[var(--text-primary)]">3-5 Powerful Questions</h2>
            </div>
            <div className="prose prose-invert max-w-none">
              <ReactMarkdown
                components={{
                  p: ({node, ...props}) => <p className="text-[var(--text-primary)] leading-relaxed mb-3" {...props} />,
                  strong: ({node, ...props}) => <strong className="font-semibold text-blue-400" {...props} />,
                  ul: ({node, ...props}) => <ul className="list-none space-y-3" {...props} />,
                  ol: ({node, ...props}) => <ol className="list-none space-y-3" {...props} />,
                  li: ({node, ...props}) => (
                    <li className="flex gap-3 text-[var(--text-primary)] bg-[var(--bg-tertiary)] p-4 rounded-lg" {...props}>
                      <HelpCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <span className="flex-1">{props.children}</span>
                    </li>
                  ),
                }}
              >
                {sessionPlan.powerful_questions || 'No questions available'}
              </ReactMarkdown>
            </div>
          </div>

          {/* Suggested Homework */}
          <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <CheckSquare className="w-5 h-5 text-green-400" />
              </div>
              <h2 className="text-xl font-semibold text-[var(--text-primary)]">Suggested Homework / Exercise</h2>
            </div>
            <div className="prose prose-invert max-w-none">
              <ReactMarkdown
                components={{
                  p: ({node, ...props}) => <p className="text-[var(--text-primary)] leading-relaxed mb-3" {...props} />,
                  strong: ({node, ...props}) => <strong className="font-semibold text-[var(--text-primary)]" {...props} />,
                  ul: ({node, ...props}) => <ul className="list-disc ml-5 space-y-2 text-[var(--text-primary)]" {...props} />,
                  li: ({node, ...props}) => <li className="text-[var(--text-primary)]" {...props} />,
                }}
              >
                {sessionPlan.suggested_homework || 'No homework suggestions available'}
              </ReactMarkdown>
            </div>
          </div>

          {/* Predicted Blind Spots */}
          <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-orange-500/10 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-orange-400" />
              </div>
              <h2 className="text-xl font-semibold text-[var(--text-primary)]">Predicted Blind Spots to Probe</h2>
            </div>
            <div className="prose prose-invert max-w-none">
              <ReactMarkdown
                components={{
                  p: ({node, ...props}) => <p className="text-[var(--text-primary)] leading-relaxed mb-3" {...props} />,
                  strong: ({node, ...props}) => <strong className="font-semibold text-orange-400" {...props} />,
                  ul: ({node, ...props}) => <ul className="list-disc ml-5 space-y-2 text-[var(--text-primary)]" {...props} />,
                  li: ({node, ...props}) => <li className="text-[var(--text-primary)]" {...props} />,
                }}
              >
                {sessionPlan.blind_spots || 'No blind spots identified'}
              </ReactMarkdown>
            </div>
          </div>

          {/* Metadata */}
          {sessionPlan.created_at && (
            <div className="text-center text-sm text-[var(--text-tertiary)] pt-4 border-t border-[var(--border-color)]">
              Generated {new Date(sessionPlan.created_at).toLocaleString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NextSessionPlan;
